import { useEffect, useState } from "react";
import { collection, doc, getDoc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type Agreement, type DocTemplate } from "@clevis/shared-types";
import { DocView, useCompanyLogo } from "./DocView";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { SignaturePad } from "./SignaturePad";
import { useAuth } from "../hooks/useAuth";

// Agreements on a job: pick a template (grouped by its library category),
// Clevis renders it against this customer/job, then the homeowner signs on
// this device or gets an email link. Signed copies download as PDFs.

type AgRow = Agreement & { id: string };
type TplRow = DocTemplate & { id: string };

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-brandorange/15 text-brandorange-dark",
  signed: "bg-success/15 text-success",
  void: "bg-steel/15 text-steel",
};

export function JobAgreements({
  tenantId,
  jobId,
  customerId,
}: {
  tenantId: string;
  jobId: string;
  customerId: string | null;
}) {
  const [companyName, setCompanyName] = useState("");
  const [rows, setRows] = useState<AgRow[]>([]);

  useEffect(() => {
    void getDoc(doc(db, paths.tenant(tenantId))).then((snap) =>
      setCompanyName((snap.get("name") as string | undefined) ?? ""),
    );
  }, [tenantId]);
  const [templates, setTemplates] = useState<TplRow[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [presenting, setPresenting] = useState<AgRow | null>(null);
  const [emailFor, setEmailFor] = useState<string | null>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    return onSnapshot(
      query(collection(db, paths.tenantAgreements(tenantId)), where("jobId", "==", jobId), orderBy("createdAt", "desc")),
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Agreement) }))),
    );
  }, [tenantId, jobId]);

  useEffect(() => {
    if (!pickerOpen) return;
    return onSnapshot(query(collection(db, paths.tenantTemplates(tenantId)), orderBy("name")), (snap) =>
      setTemplates(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocTemplate) })).filter((t) => !t.archivedAt),
      ),
    );
  }, [pickerOpen, tenantId]);

  async function create(templateId: string) {
    setBusy(true);
    setError("");
    try {
      await call("createAgreement", { tenantId, templateId, customerId, jobId });
      setPickerOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendLink(ag: AgRow) {
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    try {
      await call("emailAgreementSignLink", { tenantId, agreementId: ag.id, toEmail: email.trim() });
      setEmailFor(null);
      setEmail("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf(ag: AgRow) {
    setBusy(true);
    try {
      const res = await call<{ base64: string; filename: string }>(
        "getAgreementPdf",
        { tenantId, agreementId: ag.id },
        { mutation: false },
      );
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${res.base64}`;
      a.download = res.filename;
      a.click();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const byCategory = new Map<string, TplRow[]>();
  for (const t of templates) (byCategory.get(t.category) ?? byCategory.set(t.category, []).get(t.category)!).push(t);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Agreements ({rows.length})</CardTitle>
        <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setPickerOpen((v) => !v)}>
          {pickerOpen ? "Cancel" : "+ From template"}
        </Button>
      </div>

      {pickerOpen ? (
        <div className="mt-2 rounded-lg bg-cream p-3">
          {templates.length === 0 ? (
            <p className="text-xs text-steel">No templates yet — an admin builds them under Setup → Templates.</p>
          ) : (
            [...byCategory.entries()].map(([category, items]) => (
              <div key={category} className="mb-2 last:mb-0">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-steel">{category}</div>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((t) => (
                    <Button key={t.id} variant="ghost" className="bg-white px-2.5 py-1 text-xs" busy={busy} onClick={() => void create(t.id)}>
                      {t.name}
                    </Button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}

      <div className="mt-2">
        {rows.map((ag) => (
          <div key={ag.id} className="border-b border-steel/10 py-1.5 last:border-0">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm text-ink">
                {ag.templateName}
                <span className={cn("ml-2 rounded-full px-2 py-0.5 text-xs", STATUS_STYLE[ag.status] ?? "")}>
                  {ag.status === "signed" ? `signed — ${ag.signerName}` : ag.status}
                </span>
                {ag.sentToEmail && ag.status === "pending" ? (
                  <span className="ml-1 text-xs text-steel">sent to {ag.sentToEmail}</span>
                ) : null}
              </span>
              <span className="flex shrink-0 gap-1.5">
                {ag.status === "pending" && ag.requiresSignature ? (
                  <>
                    <Button className="px-2.5 py-1 text-xs" onClick={() => setPresenting(ag)}>
                      {ag.signer === "company" ? "Sign for company" : "Sign now"}
                    </Button>
                    {ag.signer !== "company" ? (
                      <Button
                        variant="ghost"
                        className="px-2.5 py-1 text-xs"
                        onClick={() => {
                          setEmailFor(emailFor === ag.id ? null : ag.id);
                          setEmail("");
                        }}
                      >
                        Email link
                      </Button>
                    ) : null}
                  </>
                ) : null}
                <Button variant="ghost" className="px-2.5 py-1 text-xs" busy={busy} onClick={() => void downloadPdf(ag)}>
                  PDF
                </Button>
              </span>
            </div>
            {emailFor === ag.id ? (
              <div className="mt-1.5 flex gap-2">
                <Input type="email" placeholder="homeowner@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Button busy={busy} onClick={() => void sendLink(ag)} disabled={!email.trim()}>
                  Send
                </Button>
              </div>
            ) : null}
          </div>
        ))}
        {rows.length === 0 && !pickerOpen ? <p className="py-1 text-xs text-steel">No agreements on this job.</p> : null}
      </div>
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}

      {presenting ? (
        <PresentAgreement
          tenantId={tenantId}
          agreement={presenting}
          companyName={companyName}
          onExit={() => setPresenting(null)}
        />
      ) : null}
    </Card>
  );
}

// Full-screen present mode: the rep hands the device over, the homeowner
// reads and signs. Same pattern as the on-site estimate approval.
export function PresentAgreement({
  tenantId,
  agreement,
  companyName,
  onExit,
}: {
  tenantId: string;
  agreement: Agreement & { id: string };
  companyName: string;
  onExit: () => void;
}) {
  const [step, setStep] = useState<"review" | "sign" | "done">("review");
  const logoUrl = useCompanyLogo();
  const { user } = useAuth();
  // Lien waivers and notices: someone at the company signs ("By" / "Its").
  const forCompany = agreement.signer === "company";
  const [signerName, setSignerName] = useState(forCompany ? (user?.displayName ?? "") : agreement.customerName);
  const [signerTitle, setSignerTitle] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function sign() {
    if (!signature || !agreement.signToken) return;
    setBusy(true);
    setError("");
    try {
      await call(
        "signAgreement",
        {
          tenantId,
          agreementId: agreement.id,
          token: agreement.signToken,
          signerName: signerName.trim(),
          signaturePngBase64: signature,
          ...(forCompany ? { signerTitle: signerTitle.trim() } : {}),
        },
        { mutation: false },
      );
      setStep("done");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-cream">
      <div className="mx-auto max-w-lg p-4 pb-16">
        <button className="mb-2 text-xs text-steel" onClick={onExit}>
          ← back (staff)
        </button>

        {step === "review" ? (
          <>
            <Card className="mb-4">
              <DocView title={agreement.templateName} body={agreement.body} companyName={companyName} logoUrl={logoUrl} requiresSignature={false} className="p-0 shadow-none ring-0 sm:p-0" />
            </Card>
            <Button className="w-full py-3 text-base" onClick={() => setStep("sign")}>
              Continue to sign
            </Button>
          </>
        ) : null}

        {step === "sign" ? (
          <Card>
            <h2 className="mb-1 text-lg font-bold text-ink">{forCompany ? `Sign for ${companyName}` : agreement.templateName}</h2>
            <p className="mb-3 text-sm text-steel">
              {forCompany ? "You're signing for the company. Your name and title print as \"By\" and \"Its\"." : "By signing you agree to the terms you just reviewed."}
            </p>
            <div className="mb-3">
              <Label>Your name</Label>
              <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name" autoComplete="off" />
            </div>
            {forCompany ? (
              <div className="mb-3">
                <Label>Your title ("Its")</Label>
                <Input value={signerTitle} maxLength={120} onChange={(e) => setSignerTitle(e.target.value)} placeholder="Owner, Office manager…" />
              </div>
            ) : null}
            <SignaturePad onChange={setSignature} />
            {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setStep("review")}>
                Back
              </Button>
              <Button className="flex-1 py-3" onClick={() => void sign()} busy={busy} disabled={!signature || signerName.trim().length < 2 || (forCompany && signerTitle.trim().length < 2)}>
                Sign
              </Button>
            </div>
          </Card>
        ) : null}

        {step === "done" ? (
          <Card className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-2xl">✓</div>
            <h2 className="mb-1 text-lg font-bold text-ink">{forCompany ? "Signed" : "Signed — thank you!"}</h2>
            <p className="mb-4 text-sm text-steel">
              {signerName} signed the {agreement.templateName.toLowerCase()}
              {forCompany ? ` for ${companyName}. Download the PDF to send it.` : "."}
            </p>
            <Button variant="ghost" className="w-full" onClick={onExit}>
              {forCompany ? "Done" : `Done — hand back to ${companyName}`}
            </Button>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
